'use client';

import React, { useEffect, useState } from 'react';
import { Eye, Bookmark, Share2, ExternalLink, PlayCircle, Clock, RefreshCw, Send, Loader2, RotateCcw, AlertCircle } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';

interface Reel {
  id: string;
  title: string;
  publishedAt: string;
  views: string;
  saves: string;
  shares: string;
  status: 'published' | 'queued' | 'rendering';
  thumbnailColor: string;
  duration: string;
  videoUrl?: string;
  caption?: string;
}

interface IgReel {
  id: string;
  caption?: string;
  timestamp: string;
  media_type: string;
  thumbnail_url?: string;
  permalink?: string;
  like_count?: number;
  comments_count?: number;
}

const THUMBNAIL_COLORS = [
  'from-violet-900 to-violet-700',
  'from-cyan-900 to-cyan-700',
  'from-indigo-900 to-indigo-700',
  'from-purple-900 to-purple-700',
  'from-blue-900 to-blue-700',
  'from-teal-900 to-teal-700',
  'from-rose-900 to-rose-700',
  'from-amber-900 to-amber-700',
];

const fallbackReels: Reel[] = [
  {
    id: 'reel-214',
    title: 'Claude 3.5 Sonnet vs GPT-4o: Which AI Writes Better Code?',
    publishedAt: 'Jul 30 · 09:14',
    views: '41.2K',
    saves: '1,840',
    shares: '620',
    status: 'published',
    thumbnailColor: 'from-violet-900 to-violet-700',
    duration: '0:52',
  },
  {
    id: 'reel-213',
    title: 'OpenAI o3 Reasoning Model Benchmarks Explained in 50s',
    publishedAt: 'Jul 29 · 10:02',
    views: '38.7K',
    saves: '1,420',
    shares: '510',
    status: 'published',
    thumbnailColor: 'from-cyan-900 to-cyan-700',
    duration: '0:49',
  },
  {
    id: 'reel-212',
    title: 'Perplexity AI Deep Research vs Gemini: 3 Key Differences',
    publishedAt: 'Jul 28 · 08:55',
    views: '52.1K',
    saves: '2,310',
    shares: '890',
    status: 'published',
    thumbnailColor: 'from-indigo-900 to-indigo-700',
    duration: '0:51',
  },
  {
    id: 'reel-215',
    title: 'Anthropic Constitutional AI — What It Actually Means',
    publishedAt: 'Queued · 14:00',
    views: '—',
    saves: '—',
    shares: '—',
    status: 'queued',
    thumbnailColor: 'from-zinc-800 to-zinc-700',
    duration: '0:48',
    videoUrl: 'https://storage.contentpipeline.ai/reels/reel-215.mp4',
    caption: 'Anthropic trained Claude to refuse its own creators 🤖 Here\'s how Constitutional AI actually works. #Anthropic #Claude #ConstitutionalAI #AISafety #AI',
  },
  {
    id: 'reel-216',
    title: 'LLM Hallucination Rate Drop: New RLHF Technique Breakdown',
    publishedAt: 'Rendering...',
    views: '—',
    saves: '—',
    shares: '—',
    status: 'rendering',
    thumbnailColor: 'from-zinc-800 to-zinc-600',
    duration: '0:53',
  },
  {
    id: 'reel-211',
    title: 'Mistral 7B vs Llama 3.1: Edge Deployment Comparison',
    publishedAt: 'Jul 27 · 11:30',
    views: '29.4K',
    saves: '1,120',
    shares: '380',
    status: 'published',
    thumbnailColor: 'from-purple-900 to-purple-700',
    duration: '0:50',
  },
];

function formatPublishTime(isoTimestamp: string): string {
  const date = new Date(isoTimestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) {
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = date.getDate();
    const hours = String(date.getHours()).padStart(2, '0');
    const mins = String(date.getMinutes()).padStart(2, '0');
    return `${month} ${day} · ${hours}:${mins}`;
  }
  const month = date.toLocaleString('en-US', { month: 'short' });
  const day = date.getDate();
  const year = date.getFullYear();
  return `${month} ${day}, ${year}`;
}

function mapIgReelToReel(igReel: IgReel, index: number): Reel {
  const caption = igReel.caption ?? '';
  const title = caption.length > 80 ? caption.slice(0, 77) + '…' : caption || `Reel ${igReel.id.slice(-6)}`;
  return {
    id: igReel.id,
    title,
    publishedAt: formatPublishTime(igReel.timestamp),
    views: igReel.like_count != null ? `${igReel.like_count.toLocaleString()}` : '—',
    saves: '—',
    shares: '—',
    status: 'published',
    thumbnailColor: THUMBNAIL_COLORS[index % THUMBNAIL_COLORS.length],
    duration: '—',
  };
}

type PublishState = 'idle' | 'publishing' | 'success' | 'failed';

export default function RecentReelsGrid() {
  const [reels, setReels] = useState<Reel[]>(fallbackReels);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<string>('');
  const [publishingAll, setPublishingAll] = useState(false);

  // Per-reel publish state tracking
  const [publishStates, setPublishStates] = useState<Record<string, PublishState>>({});
  const [publishErrors, setPublishErrors] = useState<Record<string, string>>({});

  // Helper: persist aggregated publish health to localStorage and notify listeners
  const syncPublishHealth = (states: Record<string, PublishState>, reelsList: Reel[]) => {
    try {
      const vals = Object.values(states);
      const successCount = vals.filter((v) => v === 'success').length;
      const failureCount = vals.filter((v) => v === 'failed').length;
      // Queue depth = reels that are queued but not yet attempted
      const queueDepth = reelsList.filter(
        (r) => r.status === 'queued' && (!states[r.id] || states[r.id] === 'idle')
      ).length;
      const payload = { successCount, failureCount, queueDepth, updatedAt: new Date().toISOString() };
      localStorage.setItem('publish_health', JSON.stringify(payload));
      window.dispatchEvent(new CustomEvent('publish:update'));
    } catch { /* ignore */ }
  };

  const fetchReels = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/meta/reels');
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error ?? 'Failed to load reels');
        setReels(fallbackReels);
      } else if (data.reels && data.reels.length > 0) {
        setReels(data.reels.slice(0, 6).map(mapIgReelToReel));
        setLastFetched(new Date().toISOString());
      } else {
        setReels(fallbackReels);
      }
    } catch {
      setError('Network error — showing cached data');
      setReels(fallbackReels);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReels();
  }, []);

  const handlePublish = async (reel: Reel) => {
    if (!reel.videoUrl || !reel.caption) {
      setPublishStates((prev) => {
        const next = { ...prev, [reel.id]: 'failed' as PublishState };
        syncPublishHealth(next, reels);
        return next;
      });
      setPublishErrors((prev) => ({ ...prev, [reel.id]: 'Missing video or caption — cannot publish' }));
      return;
    }

    setPublishStates((prev) => ({ ...prev, [reel.id]: 'publishing' }));
    setPublishErrors((prev) => {
      const next = { ...prev };
      delete next[reel.id];
      return next;
    });

    try {
      const res = await fetch('/api/meta/publish', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          videoUrl: reel.videoUrl,
          caption: reel.caption,
          shareToFeed: true,
          platforms: ['instagram'],
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }

      setPublishStates((prev) => {
        const next = { ...prev, [reel.id]: 'success' as PublishState };
        syncPublishHealth(next, reels);
        return next;
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Publish failed';
      setPublishStates((prev) => {
        const next = { ...prev, [reel.id]: 'failed' as PublishState };
        syncPublishHealth(next, reels);
        return next;
      });
      setPublishErrors((prev) => ({ ...prev, [reel.id]: message }));
    }
  };

  // Publish all queued reels that have videoUrl + caption (push past videos)
  const handlePublishAll = async () => {
    const publishable = reels.filter(
      (r) => r.status === 'queued' && !!r.videoUrl && !!r.caption &&
        (!publishStates[r.id] || publishStates[r.id] === 'idle' || publishStates[r.id] === 'failed')
    );
    if (publishable.length === 0) return;
    setPublishingAll(true);
    // Publish sequentially to avoid rate-limiting
    for (const reel of publishable) {
      await handlePublish(reel);
      // Small delay between publishes to respect IG rate limits
      await new Promise((r) => setTimeout(r, 2000));
    }
    setPublishingAll(false);
  };

  const canPublish = (reel: Reel) =>
    reel.status === 'queued' && !!reel.videoUrl && !!reel.caption;

  const getPublishState = (reelId: string): PublishState =>
    publishStates[reelId] ?? 'idle';

  return (
    <div className="card-elevated p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Recent Reels</h2>
          <p className="text-xs text-muted-foreground font-mono">
            {loading
              ? 'Fetching from Instagram…'
              : error
              ? 'Showing cached data'
              : lastFetched
              ? `Live · synced ${formatPublishTime(lastFetched)}`
              : 'Latest 6 from pipeline'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Publish All Queued button — pushes all past/pending videos */}
          {reels.some((r) => r.status === 'queued' && !!r.videoUrl && !!r.caption && (!publishStates[r.id] || publishStates[r.id] === 'idle' || publishStates[r.id] === 'failed')) && (
            <button
              onClick={handlePublishAll}
              disabled={publishingAll}
              className="flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80 bg-primary/10 hover:bg-primary/20 border border-primary/20 px-2.5 py-1 rounded-md transition-all disabled:opacity-50"
              title="Publish all queued reels to Instagram"
            >
              {publishingAll ? (
                <>
                  <Loader2 size={11} className="animate-spin" />Publishing All…
                </>
              ) : (
                <>
                  <Send size={11} />Publish All · 9:30AM IST
                </>
              )}
            </button>
          )}
          <button
            onClick={fetchReels}
            disabled={loading}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors p-1 rounded disabled:opacity-40"
            title="Refresh reels"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          </button>
          <button className="text-xs text-primary hover:text-primary/80 font-medium transition-colors flex items-center gap-1">
            View all <ExternalLink size={11} />
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 px-3 py-2 rounded-md bg-amber-500/10 border border-amber-500/20 text-xs text-amber-400 font-mono">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {reels.map((reel) => {
          const publishState = getPublishState(reel.id);
          const publishError = publishErrors[reel.id];
          const isPublishing = publishState === 'publishing';
          const isSuccess = publishState === 'success';
          const isFailed = publishState === 'failed';

          // Determine badge variant based on real-time publish state
          const badgeVariant: 'published' | 'queued' | 'rendering' | 'error' = isSuccess ?'published': isFailed ?'error' :
            reel.status === 'queued' ? 'queued' :
            reel.status === 'rendering'? 'rendering' : 'published';

          return (
            <div
              key={reel.id}
              className="group border border-border rounded-lg overflow-hidden hover:border-border/60 transition-all duration-200 bg-secondary"
            >
              {/* Thumbnail */}
              <div
                className={`relative h-24 bg-gradient-to-br ${reel.thumbnailColor} flex items-center justify-center`}
              >
                {isPublishing ? (
                  <Loader2 size={28} className="text-white/70 animate-spin" />
                ) : (
                  <PlayCircle size={28} className="text-white/60 group-hover:text-white/90 transition-colors" />
                )}
                {reel.duration !== '—' && (
                  <span className="absolute bottom-1.5 right-1.5 text-2xs font-mono text-white/70 bg-black/40 px-1.5 py-0.5 rounded">
                    {reel.duration}
                  </span>
                )}
                {/* Real-time status badge */}
                <span className="absolute top-1.5 left-1.5">
                  {isPublishing ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-2xs font-semibold font-mono uppercase tracking-wide bg-primary/20 text-primary border border-primary/30">
                      <Loader2 size={8} className="animate-spin" />
                      PUBLISHING
                    </span>
                  ) : (
                    <StatusBadge variant={badgeVariant} />
                  )}
                </span>
              </div>

              {/* Content */}
              <div className="p-3">
                <p className="text-xs font-medium text-foreground leading-snug line-clamp-2 mb-2">
                  {reel.title}
                </p>

                {/* Publish time */}
                <p className="flex items-center gap-1 text-2xs text-muted-foreground font-mono mb-2">
                  <Clock size={10} className="shrink-0" />
                  {isSuccess ? 'Scheduled · 9:30AM IST' : reel.publishedAt}
                </p>

                {/* Error message for failed publishes */}
                {isFailed && publishError && (
                  <div className="flex items-start gap-1.5 mb-2 px-2 py-1.5 rounded-md bg-negative/10 border border-negative/20">
                    <AlertCircle size={10} className="text-negative shrink-0 mt-0.5" />
                    <p className="text-2xs text-negative leading-tight font-mono" title={publishError}>
                      {publishError.length > 60 ? publishError.slice(0, 57) + '…' : publishError}
                    </p>
                  </div>
                )}

                {/* Success message */}
                {isSuccess && (
                  <div className="flex items-center gap-1.5 mb-2 px-2 py-1 rounded-md bg-positive/10 border border-positive/20">
                    <span className="text-positive text-2xs">✓</span>
                    <p className="text-2xs text-positive font-mono font-semibold">Scheduled 9:30AM IST</p>
                  </div>
                )}

                {/* Retry button for failed publishes */}
                {isFailed && canPublish(reel) && (
                  <button
                    onClick={() => handlePublish(reel)}
                    className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-2xs font-semibold bg-negative/10 text-negative hover:bg-negative/20 border border-negative/20 transition-all duration-150 mb-2"
                  >
                    <RotateCcw size={10} />
                    Retry Publish
                  </button>
                )}

                {/* Publish button for queued reels (idle state) */}
                {canPublish(reel) && publishState === 'idle' && (
                  <button
                    onClick={() => handlePublish(reel)}
                    className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-2xs font-semibold bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20 transition-all duration-150 mb-2"
                  >
                    <Send size={10} />
                    Publish IG · 9:30AM IST
                  </button>
                )}

                {reel.status === 'published' && !isSuccess && (
                  <div className="flex items-center gap-3 text-2xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Eye size={11} />
                      {reel.views}
                    </span>
                    <span className="flex items-center gap-1">
                      <Bookmark size={11} />
                      {reel.saves}
                    </span>
                    <span className="flex items-center gap-1">
                      <Share2 size={11} />
                      {reel.shares}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}